// PDF branding — the ONE tenant identity object every PDF generator uses.
// Replaces the 7 copies of the old company's name/address/logo/colors that
// were hardcoded across pdf-generator.js, admin, schedule, rfi, si-review,
// submittal and tender-letter (W4 threads this through all of them).

import { getOrgContext } from '../core/org.js';
import { signedUrl } from '../core/files.js';
import { CONFIG } from '../core/config.js';

let _logoDataUrl = null;
let _logoOrg = null;   // the org context the cached logo belongs to

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex ?? '');
  if (!m) return [26, 26, 46];
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Fetches the tenant logo once per org context and inlines it as a data URL so
// jsPDF can embed it (and so offline PDF builds reuse the cached image). Saving
// the org (a new logo) reloads the context, so the next call fetches again.
async function loadLogoDataUrl(sb, org) {
  if (_logoDataUrl !== null && _logoOrg === org) return _logoDataUrl || null;
  _logoOrg = org;
  if (!org.branding.logoPath) { _logoDataUrl = ''; return null; }
  try {
    const url = await signedUrl(sb, 'org-assets', org.branding.logoPath, 43200);
    const blob = await (await fetch(url)).blob();
    _logoDataUrl = await new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  } catch {
    _logoDataUrl = ''; // logo is optional — PDFs fall back to the org name
  }
  return _logoDataUrl || null;
}

export async function getPdfBrand(sb) {
  const org = getOrgContext();
  if (!org) throw new Error('getPdfBrand: org context not loaded');
  return {
    name: org.name,
    address: org.branding.address ?? '',
    phone: org.branding.phone ?? '',
    email: org.branding.email ?? '',
    logoDataUrl: await loadLogoDataUrl(sb, org),
    primaryRgb: hexToRgb(org.branding.primary),
    secondaryRgb: hexToRgb(org.branding.secondary ?? org.branding.primary),
    // closed decision #9: discreet footer on tenant PDFs (a paid-plan feature
    // flag can disable it in Phase 2)
    poweredBy: org.features.hide_powered_by ? '' : `Powered by ${CONFIG.appName} — ${CONFIG.appUrl.replace('https://', '')}`,
  };
}
